import z from "zod";

import { InsuredContactShape } from "./insured_contact";

export const ProductsLiabilityContactSchema = z.object({
	...InsuredContactShape,
	nameOfInsured: z.string().describe("The name of the insured"),
	insuredWebsite: z.url().describe("The website of the insured").nullable().default(null),
	address: z.object({
		city: z.string(),
		state: z.string().describe("State of the insured. Should be 2 letters, like TX for Texas"),
		streetAddress: z.string(),
		zip: z.string(),
	}).describe("Address of the insured"),
	brokerage: z.string().describe("the name of the brokerage submitting the submission"),
	brokerName: z.string().describe("the name of the broker submitting the submission").nullable().default(null),
	brokerEmail: z.string().describe("the email of the broker submitting the submission. Sometimes submissions are forwarded from classa.ai emails. classa.ai emails are never the broker.").nullable().default(null),
});

export const ProductsLiabilityTermsSchema = z.object({
	inceptionDate: z.iso.date().describe("the date in which the policy would start. For an existing policy, this would be the existing expiration date.").nullable().default(null),
	coverageALimit: z.number().describe("The requested bodily injury/property damage/products liability limit of the policy. Sometimes referred to as coverage A limit.").nullable().default(null),
	coverageBLimit: z.number().describe("The requested ground liability limit of the policy. Sometimes referred to as coverage B limit.").nullable().default(null),
	aggregateLimit: z.number().describe("The requested aggregate limit of the policy").nullable().default(null),
});

export const ProductsLiabilityCustomerSchema = z.object({
	customerName: z.string().describe("The name of the customer").nullable().default(null),
	percentOfSales: z.int().describe("The percentage of sales to the customer, for example 12 for 12%").nullable().default(null),
});

export const ProductsLiabilityReceiptSchema = z.object({
	description: z.string().describe("The description of the product or service").nullable().default(null),
	year: z.int().describe("The year of the receipt").nullable().default(null),
	amount: z.number().describe("The amount of the receipt").nullable().default(null),
	aviationCategory: z.enum([
		"airline",
		"fixed-wing-non-airline",
		"rotor-wing-non-airline",
		"spacecraft",
		"missiles",
		"unmanned-aerial-vehicles",
		"other",
	]).describe("The aviation category of the receipt").nullable().default(null),
	sector: z.enum([
		"civil",
		"military-or-government",
		"other",
	]).describe("The sector of the receipt").nullable().default(null),
});

export const ProductsLiabilityExposureSchema = z.object({
	usesAirportPremises: z.boolean().describe("Whether the product is used on the airport premises").nullable().default(null),
	serviceBulletin: z.boolean().describe("Whether the product is subject to the manufacturers factory service bulletin").nullable().default(null),
	airworthinessDirective: z.boolean().describe("Whether the product is subject to the airworthiness directive").nullable().default(null),
	cyberExposure: z.boolean().describe("Whether the product is subject to cyber exposure").nullable().default(null),
	productRecalls: z.boolean().describe("Whether the product is subject to product recalls").nullable().default(null),
	customers: z.array(ProductsLiabilityCustomerSchema).describe("The listed key customers of the product").default([]),
	receipts: z.array(ProductsLiabilityReceiptSchema).describe("The listed receipts for the product").default([]),
});

export const ProductsLiabilityRiskRatingSchema = z.object({
	rating: z.enum([
		"low",
		"medium",
		"high",
	]).describe("The risk rating of the product").nullable().default(null),
	reasoning: z.string().describe("The reasoning for the risk rating").nullable().default(null),
});

export const ProductsLiabilitySubmissionSchema = z.object({
	...ProductsLiabilityContactSchema.shape,
	...ProductsLiabilityTermsSchema.shape,
	exposure: ProductsLiabilityExposureSchema,
	riskRating: ProductsLiabilityRiskRatingSchema,
	lob: z.literal("product_liability"),
});

export type ProductsLiabilitySubmission = z.output<typeof ProductsLiabilitySubmissionSchema>;